<template>
  <div>
    <!-- Chatbot Icon -->
    <div class="chatbot-icon" :class="{ 'chic-animate': clickAnimation, 'smoke-active': smokeActive }" ref="chatIcon"
      @pointerdown="onPointerDown" :style="chatbotStyle">
      <div class="smoke-container">
        <span class="smoke-particle p1"></span>
        <span class="smoke-particle p2"></span>
        <span class="smoke-particle p3"></span>
      </div>
      <img src="/assets/images/chatBot.svg" alt="Chatbot" class="img-fluid chatbot-img"
        :style="{ transform: `rotate(${tiltAngle}deg)` }" />
    </div>

    <!-- Trail Particles (stay in path) -->
    <div v-for="p in trailParticles" :key="p.id" class="trail-particle" :style="p.style"></div>

    <!-- Chatbox Window -->
    <div class="chatbox shadow" v-show="chatVisible" :class="{ 'expanded': isExpanded }" ref="chatbox"
      :style="chatboxStyle">
      <!-- Header -->
      <div class="chatbox-header d-flex align-items-center justify-content-between p-3">
        <button class="btn minmax-btn p-0" @click="toggleExpand">
          <img src="/assets/images/expand-chat.svg" alt="Expand" class="maximize" v-if="!isExpanded" />
          <img src="/assets/images/minimize-3.svg" alt="minimize" class="minimize" v-if="isExpanded" />
        </button>

        <div class="chat-title d-flex align-items-center gap-1">
          <div class="rebot-icon d-flex align-items-center justify-content-center bg-white rounded-circle">
            <img src="/assets/images/rebot-icon.svg" alt="Chatbot Icon" class="img-fluid" />
          </div>
          <h5 class="chat-name mb-0 font-weight-bold">{{ Lang.get('messages.smart_school_assistant') }}</h5>
        </div>

        <button class="btn p-0 close-chat" @click="toggleChat">
          <img src="/assets/images/close-circle-white.svg" alt="Close" />
        </button>
      </div>

      <!-- Body -->
      <div class="chatbox-body p-3" ref="chatboxBody">
        <!-- Initial welcome message -->
        <div class="chatbox-message bot d-flex mb-3 gap-2" v-if="showWelcomeMessage">
          <div class="bot-image d-flex align-items-center justify-content-center">
            <img src="/assets/images/rebot-icon.svg" alt="Bot" class="img-fluid" />
          </div>
          <div class="message-content">
            <p class="mb-1 message-txt">{{ Lang.get('messages.welcome_message') }}</p>
            <div class="d-flex align-items-center justify-content-between">
              <small class="text-muted">{{ currentTime }}</small>
              <button class="btn p-0 speaker-btn" @click="playTextToSpeech(Lang.get('messages.welcome_message'), -1)"
                :class="{ 'speaking': playingMessageIndex === -1 }" :disabled="isLoadingAudio"
                :title="Lang.get('messages.listen_to_message')">
                <i class="fas fa-volume-up" v-if="playingMessageIndex !== -1"></i>
                <i class="fas fa-stop-circle" v-else-if="playingMessageIndex === -1"></i>
                <i class="fas fa-volume-up" v-else></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Dynamic messages -->
        <div v-for="(msg, index) in messages" :key="index" class="chatbox-message"
          :class="[msg.sender, 'd-flex', msg.sender === 'user' ? 'justify-content-end' : 'gap-2', 'mb-3']"
          :style="{ opacity: msg.opacity || 1, transform: msg.transform || 'translateY(0)' }">
          <div v-if="msg.sender === 'bot'" class="bot-image d-flex align-items-center justify-content-center">
            <img src="/assets/images/rebot-icon.svg" alt="Bot" class="img-fluid" />
          </div>
          <div class="message-content">
            <div v-html="msg.content" class="mb-1 message-txt chat-message-content"></div>
            <div class="d-flex align-items-center justify-content-between">
              <small class="text-muted">{{ msg.time }}</small>
              <!-- Speaker button for bot messages only -->
              <button v-if="msg.sender === 'bot' && msg.content?.length < 1000" class="btn p-0 speaker-btn"
                @click="playTextToSpeech(msg.originalText || msg.content, index)"
                :class="{ 'speaking': playingMessageIndex === index }" :disabled="isLoadingAudio"
                :title="Lang.get('messages.listen_to_message')">
                <i class="fas fa-volume-up" v-if="playingMessageIndex !== index"></i>
                <i class="fas fa-stop-circle" v-else></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="chatbox-footer d-flex align-items-center p-3 gap-2">
        <!-- Voice Button with dual images -->
        <button class="btn p-0 mic-btn" :class="{ recording: isRecording }" :disabled="isProcessing"
          :title="isRecording ? Lang.get('messages.stop_recording') : Lang.get('messages.start_recording')"
          @click="toggleVoiceRecording">
          <img src="/assets/images/microphone-2.svg" alt="microphone" class="record_disabled" />
          <img src="/assets/images/microphone-3.svg" alt="microphone" class="record_active" />
        </button>

        <!-- Chat Input Group with recording wave -->
        <div class="chat-input d-flex align-items-center flex-grow-1 px-2 border rounded position-relative"
          :class="{ recording: isRecording }">
          <input type="text" class="form-control border-0 py-1"
            :placeholder="isProcessing ? Lang.get('messages.processing') : Lang.get('messages.type_here')"
            v-model="chatInput" @keydown.enter.prevent="sendChatbotMessage" @input="onInputChange"
            :disabled="isProcessing" :class="{ 'voice-input': voiceInputStyling }" ref="chatInputRef" />

          <!-- Recording Wave Animation -->
          <div class="recording-wave" ref="recordingWave"></div>
        </div>

        <button class="btn send-msg-btn p-0 d-flex align-items-center justify-content-center rounded"
          @click="sendChatbotMessage" :disabled="!canSend" :title="Lang.get('messages.send')">
          <img src="/assets/images/Send.svg" alt="Send" />
        </button>
      </div>
    </div>
  </div>
</template>


<script>
import { mapGetters } from 'vuex';
import store from "@/store";
import MarkdownIt from 'markdown-it'
import Lang from "@/lang";
import ChatbotApi from '@api/chatbot.api';

export default {
  name: 'ChatbotComponent',
  data() {
    return {
      chatVisible: false,
      clickAnimation: false,
      chatInput: '',
      messages: [],
      isRecording: false,
      isProcessing: false,
      isExpanded: false,
      showWelcomeMessage: true,
      mediaRecorder: null,
      audioChunks: [],
      stream: null,
      voiceInputStyling: false,
      md: null,

      // Dragging properties
      iconPosition: 85, // Default position
      chatboxPosition: 0,
      dragging: false,
      moved: false,
      startX: 0,
      startPos: 0,
      pointerId: null,
      GAP: 20, // Gap between icon and chatbox

      // Text-to-Speech properties
      currentAudio: null,
      playingMessageIndex: null,
      isLoadingAudio: false,

      // Smoke animation
      smokeActive: false,
      smokeInterval: null,

      // Trail properties
      trailParticles: [],
      lastTrailTime: 0,
      tiltAngle: 0,
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUserRole']),

    currentTime() {
      return new Date().toLocaleTimeString('ar-EG', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    canSend() {
      return this.chatInput.trim().length > 0 && !this.isProcessing;
    },

    userRole() {
      return store.state.users?.role || store.getters["users/currentUserRole"];
    },

    chatbotStyle() {
      const locale = localStorage.getItem('locale') || 'ar';
      const isArabic = locale.includes('ar');

      if (isArabic) {
        // For Arabic, move to the left (inset-inline-end)
        return {
          'inset-inline-end': `${this.iconPosition}px`,
          'inset-inline-start': 'auto'
        };
      }

      // Default behavior
      return {
        'inset-inline-start': `${this.iconPosition}px`,
        'inset-inline-end': 'auto'
      };
    },

    chatboxStyle() {
      const locale = localStorage.getItem('locale') || 'ar';
      const isArabic = locale.includes('ar');

      if (isArabic) {
        return {
          'inset-inline-end': `${this.chatboxPosition}px`,
          'inset-inline-start': 'auto'
        };
      }

      return {
        'inset-inline-start': `${this.chatboxPosition}px`,
        'inset-inline-end': 'auto'
      };
    }
  },
  mounted() {
    this.initializeMarkdown();
    this.initializeRecordingWave();
    this.initializeDragging();
    this.updateChatboxPosition();

    // Handle window resize
    window.addEventListener('resize', this.updateChatboxPosition);

    // Ensure session is fully initialized before allowing chat
    this.ensureSessionReady();

    // Initial smoke trigger after 5 seconds, then every minute
    setTimeout(() => {
      this.triggerSmoke();
    }, 5000);
    this.smokeInterval = setInterval(this.triggerSmoke, 60000);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateChatboxPosition);
    this.cleanupDragging();
    this.stopCurrentAudio();
    if (this.smokeInterval) {
      clearInterval(this.smokeInterval);
    }
  },
  methods: {
    // Initialize Markdown-it with HTML support
    initializeMarkdown() {
      this.md = new MarkdownIt({
        html: true, // Enable HTML tags in source
        linkify: true, // Autoconvert URL-like text to links
        typographer: true, // Enable some language-neutral replacement + quotes beautification
        breaks: true // Convert '\n' in paragraphs into <br>
      });

      // Configure to render images properly
      this.md.renderer.rules.image = function (tokens, idx, options, env) {
        const token = tokens[idx];
        const src = token.attrGet('src');
        const alt = token.attrGet('alt') || '';
        const title = token.attrGet('title') || '';

        return `<img src="${src}" alt="${alt}" title="${title}" class="chat-image" loading="lazy" />`;
      };
    },

    // Text-to-Speech methods
    async playTextToSpeech(text, messageIndex) {
      try {
        // If currently playing this message, stop it
        if (this.playingMessageIndex === messageIndex) {
          this.stopCurrentAudio();
          return;
        }

        // Stop any currently playing audio
        this.stopCurrentAudio();

        // Set loading state
        this.isLoadingAudio = true;
        this.playingMessageIndex = messageIndex;

        // Strip HTML tags from text for TTS
        const plainText = this.stripHtml(text);

        // Make request to your TTS endpoint via API service
        const response = await ChatbotApi.textToSpeech(plainText);

        // Get audio blob from response
        const audioBlob = response.data;

        // Create audio URL and play
        const audioUrl = URL.createObjectURL(audioBlob);
        this.currentAudio = new Audio(audioUrl);

        // Set up audio event listeners
        this.currentAudio.onloadeddata = () => {
          this.isLoadingAudio = false;
        };

        this.currentAudio.onended = () => {
          this.stopCurrentAudio();
          URL.revokeObjectURL(audioUrl); // Clean up blob URL
        };

        this.currentAudio.onerror = (error) => {
          console.error('Audio playback error:', error);
          this.stopCurrentAudio();
          URL.revokeObjectURL(audioUrl);
          this.showError(Lang.get('messages.audio_playback_error'));
        };

        // Start playing
        await this.currentAudio.play();

      } catch (error) {
        console.error("TTS error:", error);
        this.isLoadingAudio = false;
        this.playingMessageIndex = null;
        this.showError(Lang.get('messages.tts_error'));
      }
    },

    stopCurrentAudio() {
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        this.currentAudio = null;
      }
      this.playingMessageIndex = null;
      this.isLoadingAudio = false;
    },

    stripHtml(html) {
      // Create a temporary element to strip HTML tags
      const temp = document.createElement('div');
      temp.innerHTML = html;
      return temp.textContent || temp.innerText || '';
    },

    initializeRecordingWave() {
      if (!this.$refs.recordingWave) return;

      const barWidth = 2;
      const gap = 2;

      const updateBars = () => {
        const waveContainer = this.$refs.recordingWave;
        if (!waveContainer) return;

        const count = Math.floor(waveContainer.offsetWidth / (barWidth + gap));
        waveContainer.innerHTML = Array.from(
          { length: count },
          () => `<span style="animation-delay:${Math.random()}s"></span>`
        ).join("");
      };

      // Initial render
      this.$nextTick(() => {
        updateBars();
      });

      // Update bars when container size changes
      if (window.ResizeObserver) {
        new ResizeObserver(updateBars).observe(this.$refs.recordingWave);
      }

      this.updateBars = updateBars;
    },

    initializeDragging() {
      // Set initial positions
      this.iconPosition = 85;
      this.updateChatboxPosition();
    },

    isRTL() {
      return getComputedStyle(document.documentElement).direction === 'rtl';
    },

    updateChatboxPosition() {
      if (!this.$refs.chatIcon || !this.$refs.chatbox) return;

      const iconW = this.$refs.chatIcon.offsetWidth;
      const boxW = this.$refs.chatbox.offsetWidth;
      const vw = window.innerWidth;

      let boxPos = this.iconPosition + iconW + this.GAP;
      if (boxPos + boxW > vw - 8) {
        boxPos = this.iconPosition - boxW - this.GAP;
      }

      this.chatboxPosition = boxPos;
    },

    onPointerDown(e) {
      if (e.button !== 0) return; // Only left mouse button

      this.dragging = true;
      this.moved = false;
      this.pointerId = e.pointerId;
      this.startX = e.clientX;
      this.startPos = this.iconPosition;

      if (this.$refs.chatIcon && this.pointerId !== null) {
        this.$refs.chatIcon.setPointerCapture(this.pointerId);
      }

      e.preventDefault();

      // Add event listeners
      document.addEventListener('pointermove', this.onPointerMove);
      document.addEventListener('pointerup', this.onPointerUp, { once: true });
      document.addEventListener('pointercancel', this.onPointerUp, { once: true });
      window.addEventListener('blur', this.onPointerUp, { once: true });
    },

    onPointerMove(e) {
      if (!this.dragging || e.buttons === 0) {
        this.onPointerUp();
        return;
      }

      const dx = e.clientX - this.startX;
      if (Math.abs(dx) > 4) {
        this.moved = true;
      }

      const sign = 1; // Always 1 because we are measuring distance from the left edge in both LTR/RTL
      const iconW = this.$refs.chatIcon?.offsetWidth || 60;
      const max = window.innerWidth - iconW - 8;

      let newPos = this.startPos + (dx * sign);
      newPos = Math.max(8, Math.min(newPos, max));

      this.iconPosition = newPos;
      this.updateChatboxPosition();

      // Calculate tilt based on drag speed/direction
      const tiltIntensity = 0.15;
      this.tiltAngle = Math.min(Math.max(dx * tiltIntensity, -20), 20);

      // Add trail particle if moving fast enough (plane contrail effect)
      const now = Date.now();
      if (this.moved && now - this.lastTrailTime > 40) {
        this.addTrailParticle();
        this.lastTrailTime = now;
      }
    },

    addTrailParticle() {
      const icon = this.$refs.chatIcon;
      if (!icon) return;

      const rect = icon.getBoundingClientRect();
      const centerX = rect.left + (rect.width / 2);
      const spawnY = rect.bottom - 10; // Emanate from bottom of icon

      const id = Math.random().toString(36).substr(2, 9);

      // Add slight randomness for "scattering" effect
      const scatterX = (Math.random() - 0.5) * 12;
      const scatterY = (Math.random() - 0.5) * 8;

      const style = {
        left: `${centerX + scatterX}px`,
        top: `${spawnY + scatterY}px`,
        opacity: 1
      };

      this.trailParticles.push({ id, style });

      // Remove after animation (2 seconds)
      setTimeout(() => {
        this.trailParticles = this.trailParticles.filter(p => p.id !== id);
      }, 2000);
    },

    onPointerUp() {
      if (!this.dragging) return;

      this.dragging = false;
      this.tiltAngle = 0; // Reset tilt

      if (this.pointerId !== null && this.$refs.chatIcon) {
        try {
          this.$refs.chatIcon.releasePointerCapture(this.pointerId);
        } catch (e) {
          // Ignore capture release errors
        }
        this.pointerId = null;
      }

      // Remove event listeners
      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      document.removeEventListener('pointercancel', this.onPointerUp);
      window.removeEventListener('blur', this.onPointerUp);

      // If not moved, toggle chat
      if (!this.moved) {
        this.toggleChat();
      }
    },

    cleanupDragging() {
      if (this.dragging) {
        this.onPointerUp();
      }
    },

    triggerSmoke() {
      if (this.chatVisible) return; // Don't smoke if chat is open
      this.smokeActive = true;
      setTimeout(() => {
        this.smokeActive = false;
      }, 4000); // Smoke duration
    },

    toggleChat() {
      // Trigger chic animation on open
      if (!this.chatVisible) {
        this.clickAnimation = true;
        setTimeout(() => { this.clickAnimation = false; }, 600);
      }

      this.chatVisible = !this.chatVisible;
      if (this.chatVisible) {
        this.showWelcomeMessage = true;
        this.messages = [];
        // Stop any playing audio when chat is toggled
        this.stopCurrentAudio();
        // Update chatbox position when showing
        this.$nextTick(() => {
          this.updateChatboxPosition();
        });
        // Add welcome message after a delay
        setTimeout(() => {
          this.appendMessage('bot', Lang.get('messages.welcome_message'));
        }, 500);
      } else {
        this.stopCurrentAudio();
        this.clearChat();
      }
    },

    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },

    setProcessingState(processing) {
      this.isProcessing = processing;
    },

    onInputChange() {
      this.clearVoiceInputStyling();
    },

    appendMessage(sender, content, time = null) {
      const messageTime = time || new Date().toLocaleTimeString('ar-EG', {
        hour: '2-digit',
        minute: '2-digit'
      });

      // Process the content with markdown-it
      let processedContent = content || '';

      if (sender === 'bot') {
        // First, render markdown
        processedContent = this.md.render(processedContent);

        // Additional processing for any remaining HTML img tags that weren't processed by markdown
        processedContent = processedContent.replace(
          /<img([^>]*?)src="([^"]*?)"([^>]*?)>/gi,
          '<img$1src="$2"$3 class="chat-image" loading="lazy" />'
        );
      }

      const message = {
        sender,
        content: processedContent,
        originalText: content, // Store original text for TTS
        time: messageTime,
        opacity: 0,
        transform: 'translateY(20px)'
      }

      this.messages.push(message);
      this.showWelcomeMessage = false;

      // Animate message appearance
      this.$nextTick(() => {
        const messageIndex = this.messages.length - 1;
        setTimeout(() => {
          if (this.messages[messageIndex]) {
            this.messages[messageIndex].opacity = 1;
            this.messages[messageIndex].transform = 'translateY(0)';
          }
        }, 50);

        this.scrollToBottom();
      });

      return message;
    },

    async sendChatbotMessage() {
      if (!this.chatInput.trim() || this.isProcessing) return;

      const message = this.chatInput.trim();
      const portal = this.userRole == 'Teacher' ? 'teacher' : 'admin';

      try {
        this.setProcessingState(true);

        this.appendMessage('user', message);
        this.chatInput = '';
        this.clearVoiceInputStyling();

        // Send request using ChatbotApi (handles CSRF via ApiService interceptors)
        const response = await ChatbotApi.send(message, portal);
        const data = response.data;

        if (data.response) {
          setTimeout(() => {
            this.appendMessage('bot', data.response);
          }, 500);
        } else {
          this.appendMessage('bot', Lang.get('messages.processing_failed'));
        }

      } catch (error) {
        console.error("API error:", error);
        this.appendMessage('bot', Lang.get('messages.server_error'));
      } finally {
        this.setProcessingState(false);
      }
    },

    // Voice Recording Methods
    async toggleVoiceRecording() {
      if (this.isProcessing) return;

      if (!this.isVoiceSupported()) {
        this.showError(Lang.get('messages.voice_not_supported'));
        return;
      }

      if (!this.isSecureContext()) {
        this.showError(Lang.get('messages.https_required'));
        return;
      }

      if (!this.isRecording) {
        await this.startRecording();
      } else {
        this.stopRecording();
      }
    },

    isVoiceSupported() {
      return !!(navigator.mediaDevices &&
        navigator.mediaDevices.getUserMedia &&
        window.MediaRecorder);
    },

    isSecureContext() {
      return window.location.protocol === 'https:' ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';
    },

    async startRecording() {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100
          }
        });

        const options = this.getSupportedMimeType();
        this.mediaRecorder = new MediaRecorder(this.stream, options);
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          this.processRecording();
        };

        this.mediaRecorder.onerror = (event) => {
          console.error('MediaRecorder error:', event.error);
          this.showError(Lang.get('messages.recording_error'));
          this.resetRecording();
        };

        this.mediaRecorder.start(100);
        this.isRecording = true;

        // Update wave bars when recording starts
        if (this.updateBars) {
          this.updateBars();
        }

        console.log('Recording started');
      } catch (error) {
        console.error('Error starting recording:', error);
        this.handleRecordingError(error);
      }
    },

    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
        this.isRecording = false;
        console.log('Recording stopped');
      }

      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
    },

    async processRecording() {
      try {
        if (this.audioChunks.length === 0) {
          this.showError(Lang.get('messages.no_audio_recorded'));
          return;
        }

        const mimeType = this.getSupportedMimeType().mimeType || 'audio/webm';
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });

        if (audioBlob.size === 0) {
          this.showError(Lang.get('messages.empty_recording'));
          return;
        }

        console.log(`Audio recorded: ${audioBlob.size} bytes, type: ${mimeType}`);
        await this.sendAudioToAPI(audioBlob);

      } catch (error) {
        console.error('Error processing recording:', error);
        this.showError(Lang.get('messages.recording_processing_error'));
      } finally {
        this.resetRecording();
      }
    },

    async sendAudioToAPI(audioBlob) {
      const formData = new FormData();
      const finalBlob = await this.convertToWAV(audioBlob) || audioBlob;
      formData.append('audio_file', finalBlob, 'recording.wav');

      try {
        this.setProcessingState(true);

        const response = await ChatbotApi.audioToText(formData);
        const data = response.data;

        if (data.status === 'success' && data.text) {
          this.fillInputWithVoiceText(data.text);
        } else {
          this.showError(data.message || Lang.get('messages.audio_to_text_failed'));
        }
      } catch (error) {
        console.error("Error sending audio:", error);
        this.showError(Lang.get('messages.audio_upload_error'));
      } finally {
        this.setProcessingState(false);
      }
    },

    async convertToWAV(blob) {
      try {
        if (blob.type === 'audio/wav') {
          return blob;
        }
        return blob;
      } catch (error) {
        console.warn('Audio conversion failed:', error);
        return blob;
      }
    },

    getSupportedMimeType() {
      const types = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/wav'
      ];

      for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) {
          return { mimeType: type };
        }
      }

      return {};
    },

    fillInputWithVoiceText(text) {
      this.chatInput = text;

      // Apply voice input styling
      this.voiceInputStyling = true;
      this.$nextTick(() => {
        if (this.$refs.chatInputRef) {
          this.$refs.chatInputRef.focus();
          this.$refs.chatInputRef.select();
        }
      });

      // Remove styling after 3 seconds
      setTimeout(() => {
        this.clearVoiceInputStyling();
      }, 3000);
    },

    clearVoiceInputStyling() {
      this.voiceInputStyling = false;
    },

    resetRecording() {
      this.isRecording = false;
      this.audioChunks = [];
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
    },

    handleRecordingError(error) {
      let errorMessage = Lang.get('messages.microphone_error');

      switch (error.name) {
        case 'NotAllowedError':
          errorMessage = Lang.get('messages.mic_permission_denied');
          break;
        case 'NotFoundError':
          errorMessage = Lang.get('messages.mic_not_found');
          break;
        case 'NotSupportedError':
          errorMessage = Lang.get('messages.voice_not_supported');
          break;
        case 'NotReadableError':
          errorMessage = Lang.get('messages.mic_in_use');
          break;
      }

      this.showError(errorMessage);
      this.resetRecording();
    },

    showError(message) {
      this.appendMessage('bot', `❌ ${message}`);
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const body = this.$refs.chatboxBody;
        if (body) {
          body.scrollTop = body.scrollHeight;
        }
      });
    },

    async ensureSessionReady() {
      // Make a lightweight request to ensure session is initialized
      try {
        await ChatbotApi.ping().catch(() => {
          // If ping endpoint doesn't exist, that's okay
          console.log("Chatbot session ping endpoint not available");
        });
      } catch (error) {
        console.log("Chatbot session initialization check skipped");
      }
    },

    async clearChat() {
      this.messages = [];
      this.showWelcomeMessage = false;
      this.stopCurrentAudio(); // Stop any playing audio

      try {
        await ChatbotApi.clear();
        console.log("Chatbot session cleared.");
      } catch (error) {
        console.warn("Could not clear chatbot session:", error);
      }
    }
  }
};
</script>



<style scoped>
.chatbot-icon {
  position: fixed;
  bottom: 20px;
  cursor: pointer;
  z-index: 1001;
  touch-action: none;
  user-select: none;
  animation: chatbotFloat 3s ease-in-out infinite;
  transition: all 0.3s ease;
}

.chatbot-icon.chic-animate {
  animation: chicPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.chatbot-img {
  transition: transform 0.1s ease-out;
  display: block;
}

/* Smoke Styles */
.smoke-container {
  position: absolute;
  bottom: -10px;
  /* Position at the bottom */
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 40px;
  pointer-events: none;
  z-index: 2;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.smoke-active .smoke-container {
  opacity: 1;
}

.smoke-particle {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 12px;
  height: 12px;
  background: rgba(40, 182, 190, 0.6);
  /* Color #28b6be */
  filter: blur(5px);
  border-radius: 50%;
  opacity: 0;
  transform: translateX(-50%) translateY(0) scale(0.5);
}

.smoke-active .smoke-particle.p1 {
  animation: smokeMove 3s ease-out infinite;
  animation-delay: 0s;
}

.smoke-active .smoke-particle.p2 {
  animation: smokeMove 3s ease-out infinite;
  animation-delay: 0.8s;
}

.smoke-active .smoke-particle.p3 {
  animation: smokeMove 3s ease-out infinite;
  animation-delay: 1.6s;
}

.trail-particle {
  position: fixed;
  pointer-events: none;
  width: 18px;
  height: 18px;
  background: rgba(40, 182, 190, 0.45);
  /* Color #28b6be */
  filter: blur(7px);
  border-radius: 50%;
  z-index: 9999;
  transform: translateX(-50%);
  animation: trailRiseAndFade 2s ease-out forwards;
}

@keyframes trailRiseAndFade {
  0% {
    opacity: 0.6;
    transform: translateX(-50%) scale(0.6) translateY(0);
  }

  30% {
    opacity: 0.4;
    transform: translateX(-50%) scale(1.2) translateY(-15px);
  }

  100% {
    opacity: 0;
    transform: translateX(-50%) scale(2.5) translateY(-40px);
  }
}

@keyframes smokeMove {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(0) scale(0.5);
  }

  20% {
    opacity: 0.6;
  }

  60% {
    opacity: 0.3;
    transform: translateX(-80%) translateY(-25px) scale(1.2);
  }

  100% {
    opacity: 0;
    transform: translateX(-20%) translateY(-50px) scale(2);
  }
}

@keyframes chicPop {
  0% {
    transform: scale(1);
  }

  40% {
    transform: scale(0.8) translateY(10px);
  }

  80% {
    transform: scale(1.1) translateY(-5px);
  }

  100% {
    transform: scale(1) translateY(0);
  }
}

@keyframes chatbotFloat {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-12px);
  }
}

.chatbox {
  display: block;
  position: fixed;
  bottom: 20px;
  width: 350px;
  max-height: 500px;
  background: white;
  border-radius: 10px;
  z-index: 1000;
  transition: all 0.3s ease;
}

.chatbox[style*="display: block"] {
  display: block !important;
}

.chatbox-body {
  overflow-y: auto;
}

.chatbox-message {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

/* Chat message content styling */
.chat-message-content {
  line-height: 1.5;
  word-wrap: break-word;
}

.chat-message-content p {
  margin-bottom: 0.5rem;
}

.chat-message-content p:last-child {
  margin-bottom: 0;
}

/* Image styling in chat messages */
.chat-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 8px 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: block;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.chat-image:hover {
  transform: scale(1.02);
}

/* For mobile devices, limit image width */
@media (max-width: 768px) {
  .chat-image {
    max-width: 250px;
  }
}

/* Speaker button styling */
.speaker-btn {
  color: #6c757d;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  margin-left: 8px;
}

.speaker-btn:hover {
  color: #007bff;
  transform: scale(1.1);
}

.speaker-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.speaker-btn.speaking {
  color: #007bff;
  animation: pulse 1.5s infinite;
}

.speaker-btn.speaking i {
  animation: soundWave 1s infinite;
}

@keyframes soundWave {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.2);
  }
}

/* Updated expand styles to match HTML example */
.chatbox.expanded {
  position: fixed !important;
  inset: 20px !important;
  width: auto !important;
  max-width: none !important;
  max-height: none !important;
  height: auto !important;
  z-index: 1050 !important;
}

.chatbox.expanded .chatbox-body {
  height: calc(100vh - 200px) !important;
}

@media (max-width: 768px) {
  .chatbox.expanded {
    top: 10px !important;
    left: 10px !important;
    right: 10px !important;
    bottom: 10px !important;
  }

  .chatbox.expanded .chatbox-body {
    height: calc(100vh - 180px) !important;
  }
}

/* Expand button with dual images */
.minmax-btn {
  position: relative;
  /* Add this */
  display: inline-block;
  /* Ensure proper stacking */
}

.minmax-btn .maximize,
.minmax-btn .minimize {
  transition: opacity 0.3s ease;
}

.minmax-btn .minimize {
  opacity: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.chatbox.expanded .minmax-btn .maximize {
  opacity: 0;
}

.chatbox.expanded .minmax-btn .minimize {
  opacity: 1;
}

/* Microphone button with dual images */
.mic-btn {
  position: relative;
}

.mic-btn .record_disabled,
.mic-btn .record_active {
  transition: opacity 0.3s ease;
}

.mic-btn .record_active {
  opacity: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.mic-btn.recording .record_disabled {
  opacity: 0;
}

.mic-btn.recording .record_active {
  opacity: 1;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
  }
}

.mic-btn.recording {
  animation: pulse 1s infinite;
}

/* Recording wave animation */
.recording-wave {
  position: absolute;
  top: 20px;
  left: 0;
  right: 0;
  bottom: 0;
  display: none;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  border-radius: inherit;
  gap: 2px;
}

.chat-input.recording .recording-wave {
  display: flex;
}

.chat-input.recording input {
  opacity: 0.3;
}

.recording-wave span {
  width: 2px;
  background: #007bff;
  border-radius: 1px;
  animation: wave 1s ease-in-out infinite;
}

@keyframes wave {

  0%,
  100% {
    height: 4px;
    transform: scaleY(1);
  }

  50% {
    height: 20px;
    transform: scaleY(3);
  }
}

/* Voice input styling */
.voice-input {
  background-color: #e8f5e8 !important;
  transition: all 0.3s ease;
}

.form-control:disabled {
  background-color: #f8f9fa !important;
  cursor: not-allowed;
}

.send-msg-btn:disabled,
.mic-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-control {
  transition: all 0.3s ease;
}
</style>